import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { PopoverScripts } from "./PopoverScripts";
import "./popover.less";

interface PopoverInterface {
   content?: React.ReactNode;
   children: React.ReactNode;
   title?: string | React.ReactNode;
   trigger?: "hover" | "focus" | "click";
   placement?: "top" | "left" | "right" | "bottom";
   className?: string;
   overlayClassName?: string;
   id?: string;
   key?: string | number;
   style?: React.CSSProperties;
   overlayStyle?: React.CSSProperties;
   dark?: boolean;
   disabled?: boolean;
   customClose?: boolean;
   setCustomClose?: React.Dispatch<React.SetStateAction<boolean>>;
   disableClosingByClickOutisde?: boolean;
}
export const Popover: React.FC<PopoverInterface> = ({
   content,
   children,
   placement = "top",
   title,
   trigger = "click",
   overlayClassName = "",
   id,
   key,
   style,
   overlayStyle,
   dark = false,
   disabled = false,
   customClose,
   setCustomClose,
   disableClosingByClickOutisde = false
}) => {
   const ref = useRef<HTMLDivElement>(null);
   const childRef = useRef<HTMLDivElement>(null);
   const popContentRef = useRef<HTMLDivElement>(null);
   const [isOpen, setIsOpen] = useState<Boolean>(false);
   //This state exist because when we have trigger on hover and we want to for example copy the content from popover we dont want to close popover at the same time
   const [isCheckingContent, setIsCheckingContent] = useState<Boolean>(false);
   const [portal, setPortal] = useState<Element | null>(null);
   const [arrowCoordinates, setArrowCoordinates] = useState<React.CSSProperties>();
   const [classPlacement, setClassPlacement] = useState<string>(placement);

   useEffect(() => {
      if (document.getElementById("popover-root")) {
         setPortal(document.getElementById("popover-root"));
      }
   }, []);

   useEffect(() => {
      if (trigger !== "hover") return;
      const animationType = isCheckingContent ? "show" : "hide";
      setTimeout(() => {
         setIsOpen(isCheckingContent);
         animation(animationType);
      }, 50);
   }, [isCheckingContent]);

   useEffect(() => {
      if (!ref.current && trigger !== "hover") return;

      const handleClickOutside = (e: MouseEvent) => {
         if (
            // @ts-ignore
            (ref.current && !ref.current.contains(e.target) && !disableClosingByClickOutisde) ||
            (trigger === "click" &&
               ref.current &&
               childRef.current &&
               // @ts-ignore
               childRef.current.contains(e.target))
         ) {
            setIsOpen(false);
            animation("hide");
         }
      };

      setTimeout(() => {
         if (isOpen) {
            document.addEventListener("click", handleClickOutside);
         } else {
            document.removeEventListener("click", handleClickOutside);
         }
      }, 0);

      return () => {
         document.removeEventListener("click", handleClickOutside);
      };
   }, [ref.current, isOpen, setIsOpen]);

   const correctPopoverPositioning = () => {
      const currentPopover = ref?.current;
      const childBCRect = childRef?.current?.getBoundingClientRect();
      let finalPlacement;
      if (currentPopover && childBCRect) {
         const initialPosition = PopoverScripts.getPopoverCoordinates(
            classPlacement,
            childBCRect,
            currentPopover
         );
         finalPlacement = PopoverScripts.fixOutOfScreenPlacement(
            classPlacement,
            initialPosition,
            currentPopover
         );
         setClassPlacement(finalPlacement);
         const position = PopoverScripts.getPopoverCoordinates(
            finalPlacement,
            childBCRect,
            currentPopover
         );
         const finalPosition = PopoverScripts.fixOutOfScreenCoordinates(position, currentPopover);
         currentPopover.style.left = `${finalPosition.left}px`;
         currentPopover.style.top = `${finalPosition.top}px`;
      }
      // Set arrow coordinates
      // TODO - fix arrow coordinates on outofscreen repositioning
      if (childRef?.current && ref?.current?.offsetHeight) {
         const arrowCoordinates = PopoverScripts.getArrowCoordinates(finalPlacement, ref.current);
         setArrowCoordinates(arrowCoordinates);
      }
   };

   const openPopover = () => {
      setCustomClose && setCustomClose(false);
      setIsCheckingContent(true);
      setTimeout(() => {
         // Set popover position after render
         correctPopoverPositioning();
      }, 50);
      setIsOpen(true);
      animation("show");
   };

   const closePopover = () => {
      setIsOpen(false);
   };

   const animation = (type: string) => {
      const delay = trigger === "click" ? 0 : 50;
      setTimeout(() => {
         if (ref?.current) {
            ref.current.style.opacity = type === "show" ? "1" : "0";
         }
      }, delay);
   };

   const triggerEvents = (trigger: string): {} => {
      switch (trigger) {
         case "hover":
            return { onMouseEnter: openPopover };
         case "focus":
            return { onFocus: openPopover, onBlur: closePopover };
         default:
            return { onClick: openPopover };
      }
   };

   const handleMoveOut = () => {
      setIsCheckingContent(false);
   };

   const handleMoveIn = () => {
      setIsCheckingContent(true);
   };

   return (
      <>
         <div
            ref={childRef}
            style={{ ...style, display: "inline-block", boxSizing: "content-box" }}
            {...triggerEvents(trigger)}
            className={`popover-open ${disabled ? "disabled" : ""}`}
            //@ts-ignore
            onMouseLeave={handleMoveOut}
         >
            {children}
         </div>

         {portal &&
            createPortal(
               <div
                  ref={ref}
                  className={
                     `bp2-popover open ${dark ? "dark" : ""} ` +
                     (overlayClassName && ` ${overlayClassName}`) +
                     (placement && ` ${classPlacement}`)
                  }
                  style={{ ...overlayStyle }}
                  id={id}
                  key={key}
               >
                  {isOpen && !customClose && (
                     <>
                        <div
                           className="bp2-popover-arrow"
                           style={arrowCoordinates ? { ...arrowCoordinates } : {}}
                        ></div>
                        <div
                           className="bp2-popover-inner"
                           ref={popContentRef}
                           data-testid="popover"
                           onMouseEnter={handleMoveIn}
                           onMouseLeave={() => setIsCheckingContent(false)}
                        >
                           {title && <div className="bp2-popover-inner-title">{title}</div>}
                           {content && <div className="bp2-popover-inner-content">{content}</div>}
                        </div>
                     </>
                  )}
               </div>,
               portal
            )}
      </>
   );
};
